import React, { useCallback, useMemo, useState } from "react";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import { usePageActivity } from "Components/Hooks/UsePageActivity";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledDropdown,
} from "reactstrap";

import TableContainer from "../../Components/Common/TableContainer";
import {
  getLTAs,
  deleteLTA,
  getLTASummary,
  LTA_POPULATE,
} from "common/services/ltas.service";
import { getSortObj } from "common/lib/tableDataOptions";
import {
  createTableSearchParams,
  parseTableFilters,
  parseTableSearch,
  parseTableSort,
} from "common/lib/tableQueryParams";
import * as moment from "moment";
import Permission from "Components/Common/Permission";
import Permissions from "Components/Common/Permissions";
import DeleteModal from "Components/Common/DeleteModal";
import { normalize } from "common/lib/normalize";
import { toast } from "react-toastify";
import BreadCrumb from "Components/Common/BreadCrumb";
import BulkActionBar from "Components/Common/BulkActionBar";
import { clearTableCheckboxes } from "common/lib/tableSelection";
import { useProfile } from "Components/Hooks/UserHooks";
import { isAllowed } from "common/lib/permissionUtils";
import LTASummaryWidgets from "./components/LTASummaryWidgets";
import LTAStatusBadge from "./components/LTAStatusBadge";
import {
  FilterDateRange,
  FilterLabel,
  FilterSearchBox,
} from "Components/Common/TableFilters";
import {
  LTAAgencyFilter,
  LTAServiceFilter,
  LTAStatusFilter,
} from "./components/LTAFilters";
import {
  statusFilterConditions,
  termProgress,
} from "./components/ltaOptions";
import AddLTA from "./AddLTA";
import UpdateLTA from "./UpdateLTA";
import Import from "./Import";

interface DateRange {
  fromDate: Date | null;
  toDate: Date | null;
}

interface FilterValue {
  // Selects hold the whole `{ value, label }` option so the choice survives a
  // page reload from the URL without a second lookup.
  ltaService: any;
  ltaStatus: any;
  agency: any;
  dateRange: DateRange;
}

const defaultFilterValue: FilterValue = {
  ltaService: null,
  ltaStatus: null,
  agency: null,
  dateRange: { fromDate: null, toDate: null },
};

/**
 * A compact read-out of the agreement term: the two dates plus a bar showing
 * how far through it the agreement is.
 */
const TermCell = ({ lta }: { lta: any }) => {
  const progress = termProgress(lta?.startDate, lta?.endDate);

  if (!lta?.startDate && !lta?.endDate) {
    return <span className="text-muted">Not Provided</span>;
  }

  return (
    <div style={{ minWidth: "180px" }}>
      <div className="d-flex justify-content-between fs-12 mb-1">
        <span>
          {lta?.startDate
            ? moment(new Date(lta.startDate)).format("DD MMM Y")
            : "—"}
        </span>
        <span className="text-muted">
          {lta?.endDate ? moment(new Date(lta.endDate)).format("DD MMM Y") : "—"}
        </span>
      </div>
      {progress !== null && (
        <div className="progress" style={{ height: "4px" }}>
          <div
            className={`progress-bar bg-${
              progress >= 90 ? "danger" : progress >= 75 ? "warning" : "success"
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
};

/** The single service the agreement covers. */
const ServiceCell = ({ service }: { service: any }) => {
  if (!service?.name) {
    return <span className="text-muted">Not Provided</span>;
  }
  return (
    // Plain wrapping text at a fixed width, as in the Agreement column.
    <div
      className="fw-medium text-primary"
      style={{ whiteSpace: "pre-wrap", width: "200px" }}
    >
      {service.name}
    </div>
  );
};

const LTAs = () => {
  const location = useLocation();
  usePageActivity(location.pathname, "LTAs");

  const [searchParams, setSearchParams] = useSearchParams();

  const [params] = useState({
    page: Number(searchParams.get("page")) || 1,
    pageSize: Number(searchParams.get("pageSize")) || 20,
  });

  const [pagination, setPagination] = useState({
    pageIndex: params.page - 1,
    pageSize: params.pageSize,
  });

  const setPage = (newPage: number) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      pageIndex: newPage - 1,
    }));
  };

  const [sorted, setSorted] = useState(() =>
    parseTableSort(searchParams, [{ id: "createdAt", desc: true }])
  );

  const [selectedRecord, setSelectedRecord] = useState<any>({});

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);

  const showUpdateModalForm = (lta: any) => {
    setSelectedRecord(lta);
    setIsUpdateModalOpen(true);
  };

  const [searchValue, setSearchValue] = useState(
    parseTableSearch(searchParams)
  );
  const [filterValue, setFilterValue] = useState(() =>
    parseTableFilters(searchParams, defaultFilterValue)
  );

  const resetTable = () => {
    setPage(1);
    setSorted([{ id: "createdAt", desc: true }]);
    setSearchValue("");
    setFilterValue(defaultFilterValue);
  };

  const [selectedCheckBox, setSelectedCheckBox] = useState<any>([]);

  const onSelectCheckbox = () => {
    setSelectedCheckBox(document.querySelectorAll(".tableCheckbox:checked"));
  };

  const unCheckAll = useCallback(() => {
    clearTableCheckboxes();
    setSelectedCheckBox([]);
  }, []);

  const checkedAll = useCallback(() => {
    const checkall: any = document.getElementById("checkBoxAll");
    document.querySelectorAll(".tableCheckbox").forEach((el: any) => {
      el.checked = checkall.checked;
    });
    onSelectCheckbox();
  }, []);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState<any>({});
  const [apiParams, setApiParams] = useState<any>({});

  const onPageChange = useCallback(
    async ({
      page,
      sorted,
      searchValue,
      filterValue,
    }: {
      page: number;
      sorted: Array<{ id: string; desc: boolean }>;
      searchValue: string;
      filterValue?: any;
    }) => {
      const params = createTableSearchParams({
        page,
        pageSize: pagination.pageSize,
        sorted,
        searchValue,
        filterValue,
      });
      setSearchParams(params, { replace: true });

      const sortObj = getSortObj(sorted);

      let andConditions: any[] = [];

      if (filterValue?.ltaService?.value) {
        andConditions.push({
          ltaService: { id: { $eq: filterValue.ltaService.value } },
        });
      }
      if (filterValue?.ltaStatus?.value) {
        // Status is derived from the term, so it filters on the dates.
        andConditions.push(
          ...statusFilterConditions(filterValue.ltaStatus.value)
        );
      }
      if (filterValue?.agency?.value) {
        // Either side of the relationship counts as "this agency's LTA".
        andConditions.push({
          $or: [
            { leadAgency: { id: { $eq: filterValue.agency.value } } },
            { agencies: { id: { $eq: filterValue.agency.value } } },
          ],
        });
      }
      if (filterValue?.dateRange?.fromDate && filterValue?.dateRange?.toDate) {
        const fromDate = new Date(
          filterValue.dateRange.fromDate
        ).toLocaleDateString("en-CA");
        const toDate = new Date(
          filterValue.dateRange.toDate
        ).toLocaleDateString("en-CA");
        // Agreements that overlap the selected window at any point.
        andConditions.push({
          startDate: { $lte: toDate },
          endDate: { $gte: fromDate },
        });
      }

      andConditions.push({
        $or: [
          { title: { $containsi: searchValue } },
          { ltaNumber: { $containsi: searchValue } },
          { contractor: { contractorName: { $containsi: searchValue } } },
        ],
      });

      try {
        setLoading(true);
        unCheckAll();
        const apiParams = {
          pagination: { page, pageSize: pagination.pageSize },
          sort: sortObj,
          populate: [...LTA_POPULATE, "contracts"],
          filters: { $and: andConditions },
        };
        setApiParams(apiParams);
        const response: any = await getLTAs(apiParams);
        setData(normalize(response.data));
        setMeta(response?.meta);
      } catch (e: any) {
        toast.error("Error while fetching data", {});
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize, setSearchParams, unCheckAll]
  );

  const [deleteModal, setDeleteModal] = useState(false);

  const onClickDelete = useCallback(
    (data: any) => {
      setSelectedRecord(data);
      setDeleteModal(true);
    },
    [setSelectedRecord, setDeleteModal]
  );

  const fetchUpdatedData = useCallback(() => {
    onPageChange({
      page: pagination.pageIndex + 1,
      sorted: sorted,
      searchValue: searchValue,
      filterValue: filterValue,
    });
  }, [onPageChange, pagination.pageIndex, sorted, searchValue, filterValue]);

  const [deleting, setDeleting] = useState(false);
  const handleDelete = async () => {
    if (selectedRecord) {
      setDeleting(true);
      try {
        await deleteLTA(selectedRecord.id);
        fetchUpdatedData();
        setDeleteModal(false);
        toast.success("LTA deleted successfully");
      } catch (e: any) {
        toast.error("Error while deleting LTA");
      } finally {
        setDeleting(false);
      }
    }
  };

  /* Delete Multiple */
  const [deleteMultipleModal, setDeleteMultipleModal] = useState(false);
  const handleDeleteMultiple = async () => {
    const selectedIds = Array.from(selectedCheckBox).map((el: any) => el.value);
    if (selectedIds.length > 0) {
      setDeleting(true);
      try {
        await Promise.all(selectedIds.map((id: any) => deleteLTA(id)));
        toast.success("LTAs deleted successfully.");
        fetchUpdatedData();
        setDeleteMultipleModal(false);
      } catch (e: any) {
        toast.error("Error while deleting data.");
      } finally {
        setDeleting(false);
      }
    }
  };

  const { userProfile } = useProfile();
  // The select column is pointless without the permission to act on it.
  const [columnVisibility] = useState({
    checkBoxColumn: isAllowed(userProfile?.permissions, [
      { resource: "lta", action: "delete" },
    ]),
  });

  const columns = useMemo(
    () => [
      {
        header: (
          <input
            type="checkbox"
            id="checkBoxAll"
            className="form-check-input"
            onClick={() => checkedAll()}
          />
        ),
        cell: (cell: any) => (
          <input
            type="checkbox"
            className="tableCheckbox form-check-input"
            value={cell.getValue()}
            onChange={() => onSelectCheckbox()}
          />
        ),
        id: "checkBoxColumn",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: false,
        meta: { cellClass: "table-checkbox-column" },
      },
      {
        header: "Agreement",
        accessorKey: "title",
        id: "title",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const row = cell.row.original;
          return (
            <div style={{ whiteSpace: "pre-wrap", width: "300px" }}>
              <Link
                to={`/ltas/${row?.id}`}
                className="fw-medium link-primary d-block"
              >
                {cell.getValue() || "Untitled agreement"}
              </Link>
              <span className="text-muted fs-12">
                <i className="ri-hashtag align-bottom"></i>
                {row?.ltaNumber || "No number"}
              </span>
            </div>
          );
        },
      },
      {
        header: "Contractor",
        accessorKey: "contractor.contractorName",
        id: "contractor.contractorName",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const contractor = cell.row.original?.contractor;
          if (!contractor) return <span className="text-muted">Not Provided</span>;
          return (
            <Link
              to={`/contractors/${contractor.id}`}
              className="text-body fw-medium"
              style={{ whiteSpace: "pre-wrap", display: "inline-block", width: "200px" }}
            >
              {contractor.contractorName}
            </Link>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "endDate",
        id: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <LTAStatusBadge
            startDate={cell.row.original?.startDate}
            endDate={cell.row.original?.endDate}
            showCountdown
          />
        ),
      },
      {
        header: "Term",
        accessorKey: "startDate",
        id: "startDate",
        enableColumnFilter: false,
        cell: (cell: any) => <TermCell lta={cell.row.original} />,
      },
      {
        header: "LTA Service",
        id: "ltaService",
        accessorKey: "ltaService",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => <ServiceCell service={cell.getValue()} />,
      },
      {
        header: "Lead Agency",
        accessorKey: "leadAgency.name",
        id: "leadAgency.name",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const row = cell.row.original;
          const others = row?.agencies?.length || 0;
          return (
            <div>
              <div>{cell.getValue() || "Not Provided"}</div>
              {row?.isUsableByOtherAgencies && (
                <span className="badge bg-info-subtle text-info fs-11 mt-1">
                  <i className="ri-share-forward-line align-bottom me-1"></i>
                  Open to all
                </span>
              )}
              {!row?.isUsableByOtherAgencies && others > 0 && (
                <span className="text-muted fs-12 d-block mt-1">
                  +{others} using
                </span>
              )}
            </div>
          );
        },
      },
      {
        header: "Contracts",
        id: "contracts",
        accessorKey: "contracts",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const contracts = cell.getValue() || [];
          const total = contracts.reduce(
            (sum: number, c: any) => sum + (c?.contractValue || 0),
            0
          );
          return (
            <div>
              <span className="fw-medium">{contracts.length}</span>
              {total > 0 && (
                <div className="text-muted fs-12">
                  ${total.toLocaleString()}
                </div>
              )}
            </div>
          );
        },
      },
      {
        header: "Action",
        enableSorting: false,
        cell: (cellProps: any) => {
          const rowData = cellProps.row.original;
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                href="#"
                className="btn btn-soft-secondary btn-sm dropdown"
                tag="button"
              >
                <i className="ri-more-fill align-middle"></i>
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end">
                <Permission resource="lta" action="findOne">
                  <DropdownItem href={`/ltas/${rowData.id}`}>
                    <i className="ri-eye-fill align-bottom me-2 text-muted"></i>{" "}
                    View
                  </DropdownItem>
                </Permission>

                <Permission resource="lta" action="update">
                  <DropdownItem
                    href="/#"
                    onClick={(e) => {
                      e.preventDefault();
                      showUpdateModalForm({ id: rowData.id, ...rowData });
                    }}
                  >
                    <i className="ri-pencil-fill align-bottom me-2 text-muted"></i>{" "}
                    Edit
                  </DropdownItem>
                </Permission>

                <Permission resource="lta" action="delete">
                  <DropdownItem divider />
                  <DropdownItem
                    href="#"
                    onClick={() => {
                      onClickDelete(rowData);
                    }}
                  >
                    <i className="ri-delete-bin-fill align-bottom me-2 text-muted"></i>{" "}
                    Delete
                  </DropdownItem>
                </Permission>
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [onClickDelete, checkedAll]
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="LTAs"
            pageTitles={[{ title: "Dashboard", link: "/dashboard" }]}
          />
          <DeleteModal
            show={deleteModal}
            onDeleteClick={() => handleDelete()}
            onCloseClick={() => setDeleteModal(false)}
            loading={deleting}
          />

          <LTASummaryWidgets fetchSummary={getLTASummary} />

          <Card>
            <CardBody>
              <Row className="g-3 align-items-end">
                <Col xxl={3} lg={6}>
                  <FilterSearchBox
                    id="lta-search"
                    placeholder="Search by title, number or contractor..."
                    value={searchValue}
                    onChange={(value: string) => {
                      setSearchValue(value);
                      setPage(1);
                    }}
                  />
                </Col>
                <Col xxl={2} lg={6}>
                  <FilterLabel htmlFor="ltaServiceFilter">LTA Service</FilterLabel>
                  <LTAServiceFilter
                    onChange={(value: any) => {
                      setFilterValue({ ...filterValue, ltaService: value });
                      setPage(1);
                    }}
                    value={filterValue.ltaService}
                  />
                </Col>
                <Col xxl={2} lg={6}>
                  <FilterLabel htmlFor="ltaStatusFilter">Status</FilterLabel>
                  <LTAStatusFilter
                    onChange={(value: any) => {
                      setFilterValue({ ...filterValue, ltaStatus: value });
                      setPage(1);
                    }}
                    value={filterValue.ltaStatus}
                  />
                </Col>
                <Col xxl={2} lg={6}>
                  <FilterLabel htmlFor="ltaAgencyFilter">UN Agency</FilterLabel>
                  <LTAAgencyFilter
                    onChange={(value: any) => {
                      setFilterValue({ ...filterValue, agency: value });
                      setPage(1);
                    }}
                    value={filterValue.agency}
                  />
                </Col>
                <Col xxl={3} lg={6}>
                  <FilterDateRange
                    id="lta-date-range"
                    label="Active Between"
                    value={filterValue?.dateRange}
                    onChange={(dateRange: any) => {
                      setFilterValue({ ...filterValue, dateRange });
                      setPage(1);
                    }}
                  />
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <Row className="align-items-center g-3">
                <Col md={9}>
                  <h5 className="card-title mb-1">Long Term Agreements</h5>
                  <p className="text-muted mb-0">
                    Framework agreements established with contractors, vendors
                    and implementing partners by UN agencies. Contracts awarded
                    under an agreement are linked to it.
                  </p>
                </Col>
                <div className="col-md-auto ms-auto">
                  <div className="d-flex gap-2">
                    <Permission resource="lta" action="create">
                      <Link
                        to="#"
                        className="btn btn-primary"
                        onClick={(e) => {
                          e.preventDefault();
                          setIsAddModalOpen(true);
                        }}
                      >
                        <i className="ri-add-line align-bottom me-1"></i>
                        Add LTA
                      </Link>
                    </Permission>

                    <Permissions
                      checks={[
                        { resource: "lta", action: "import" },
                        { resource: "lta", action: "exportToCsv" },
                      ]}
                    >
                      <Import apiParams={apiParams} onImported={fetchUpdatedData} />
                    </Permissions>
                  </div>
                </div>
              </Row>
            </CardHeader>
            <CardBody>
              <TableContainer
                loading={loading}
                columns={columns}
                data={data || []}
                pagination={pagination}
                setPagination={setPagination}
                sorted={sorted}
                setSorted={setSorted}
                onPageChange={onPageChange}
                FilterSection={() => {}}
                totalRows={meta?.pagination?.total || 0}
                totalPages={meta?.pagination?.pageCount || 0}
                searchValue={searchValue}
                filterValue={filterValue}
                columnVisibility={columnVisibility}
                divClass="table-responsive table-card mb-4"
                tableClass="align-middle mb-0 table-striped"
                theadClass="table-light table-nowrap"
              />
              <AddLTA
                toggle={() => setIsAddModalOpen((state) => !state)}
                isModalOpen={isAddModalOpen}
                fetchData={resetTable}
              />
              <UpdateLTA
                toggle={() => setIsUpdateModalOpen((state) => !state)}
                isModalOpen={isUpdateModalOpen}
                selectedRecord={selectedRecord}
                fetchData={fetchUpdatedData}
              />
              <DeleteModal
                show={deleteMultipleModal}
                onDeleteClick={() => handleDeleteMultiple()}
                onCloseClick={() => setDeleteMultipleModal(false)}
                loading={deleting}
              />
              <Permissions checks={[{ resource: "lta", action: "delete" }]}>
                <BulkActionBar
                  count={selectedCheckBox.length}
                  onClear={unCheckAll}
                  itemLabel="LTA"
                >
                  <Button
                    color="danger"
                    size="sm"
                    className="material-shadow-none"
                    onClick={() => setDeleteMultipleModal(true)}
                  >
                    <i className="ri-delete-bin-line align-bottom me-1"></i>{" "}
                    Delete
                  </Button>
                </BulkActionBar>
              </Permissions>
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default LTAs;
